<!-- 实勘交易所 -->
<script setup lang="ts">
const { t, localePath } = useAppI18n()

const SCORE_STAR_ASSET = 'https://resources1.interface003.com/web30/2026/06/16/5c03a3c1-306a-474f-9267-2f40fd9d866b.png'

type SurveyRecentTraderLabel = {
  backgroundColor?: string
  content?: string
  fontColor?: string
}

type SurveyRecentTrader = {
  code: string
  logo?: string
  ico?: string
  flag?: string
  englishName?: string
  chineseName?: string
  company?: string
  annotation?: string
  abbreviation?: string
  color?: string
  score?: number | string
  labels?: string[]
  specialtyLabels?: SurveyRecentTraderLabel[]
  alias: string
  showName?: string
}

/** 组件入参，最近实勘交易商列表由父组件传入。 */
const props = withDefaults(
  defineProps<{
    surveyList?: SurveyRecentTrader[]
  }>(),
  {
    surveyList: () => [],
  },
)

const DEFAULT_STATUS_COLOR = '#338066'

/** 右侧栏控制条数，避免列表过长挤压下载卡片。 */
const visibleList = computed(() => props.surveyList.filter((item) => item?.alias).slice(0, 5))

/** 优先展示品牌短名，缺失时回退公司名和 code。 */
/**
 * 获取交易商展示名称。
 * @param item 单个交易商数据。
 * @returns 优先级回退后的展示文案。
 */
const getTraderName = (item: SurveyRecentTrader) =>
  item.showName || item.englishName || item.chineseName || item.company || item.code

/**
 * 提取状态文案和颜色，优先沿用接口已有的监管/风险描述。
 * @param item 单个交易商数据。
 * @returns 展示在第二行左侧的状态信息。
 */
const getStatusMeta = (item: SurveyRecentTrader) => ({
  text: item.annotation || item.abbreviation || '',
  color: item.color || DEFAULT_STATUS_COLOR,
})

/**
 * 提取附加元信息，优先展示年限类标签，缺失时回退其他首个标签。
 * @param item 单个交易商数据。
 * @returns 展示在第二行右侧的补充信息。
 */
const getSecondaryMeta = (item: SurveyRecentTrader) => {
  return (item && item.labels && item.labels[101] && item.labels[101][0]) || ''
}

/**
 * 统一格式化评分。
 * @param score 原始评分，可能为数字或字符串。
 * @returns 保留两位小数的评分字符串，缺失时返回 `--`。
 */
const formatScore = (score?: number | string) => {
  const value = Number(score)
  return Number.isFinite(value) ? value.toFixed(2) : '--'
}

const route = useRoute()
const { setBreadcrumb } = useLsBreadcrumb()
const handleClick = (item: SurveyRecentTrader) => {
  setBreadcrumb(item.alias || item.code, {
    labelKey: '82521', // 走翻译的值
    route: route.fullPath,
  })
}
</script>

<template>
  <aside
    v-if="visibleList.length"
    class="flex w-full flex-col rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-white px-[24px] pt-[8px] pb-[16px]"
  >
    <div class="flex items-center border-b-[0.5px] border-[rgba(5,21,36,0.06)] py-[16px]">
      <h3 class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">
        {{ t('82535') }}
      </h3>
    </div>
    <div class="pt-[12px]">
      <NuxtLink
        v-for="item in visibleList"
        :key="item.code"
        :to="localePath({ name: 'exchange-detail', params: { slug: item.alias || item.code } })"
        target="_blank"
        class="flex cursor-pointer items-center gap-[8px] py-[12px] hover:bg-[rgba(0,0,0,0.03)]"
        @click="handleClick(item)"
      >
        <div class="flex size-[32px] shrink-0 overflow-hidden rounded-[4px] bg-[#f5f7fb]">
          <CommonImage
            v-if="item.ico"
            :src="item.ico"
            :alt="getTraderName(item)"
            class="size-full object-cover"
            loading="eager"
          />
        </div>

        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-[8px]">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-[4px]">
                <p class="truncate text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                  {{ getTraderName(item) }}
                </p>
              </div>

              <div class="mt-[2px] flex flex-wrap items-center gap-[8px]">
                <span
                  v-if="getStatusMeta(item).text"
                  class="text-[11px] leading-[16px] font-[400]"
                  :style="{
                    color: getStatusMeta(item).color,
                  }"
                >
                  {{ getStatusMeta(item).text }}
                </span>

                <span
                  v-if="getStatusMeta(item).text && getSecondaryMeta(item)"
                  class="h-[8px] w-[1px] bg-[rgba(5,21,36,0.06)]"
                />

                <span v-if="getSecondaryMeta(item)" class="text-[11px] leading-[16px] font-[400] text-[#505258]">
                  {{ getSecondaryMeta(item) }}
                </span>
              </div>
            </div>

            <div class="inline-flex shrink-0 items-center gap-[4px] rounded-[1000px] bg-[#f8f8f8] px-[12px] py-[4px]">
              <CommonImage :src="SCORE_STAR_ASSET" alt="star" class="flex size-[14px] shrink-0" loading="eager" />
              <span class="text-center font-['WikiFX-Medium'] text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                {{ formatScore(item.score) }}
              </span>
            </div>
          </div>
        </div>
      </NuxtLink>
    </div>
  </aside>
</template>

<style scoped></style>
